import { useMemo } from "react";
import { useRecoilValue } from "recoil";
import { EPreferences } from "@/enums/prefernces";
import {
  DateFormatPreference,
  DEFAULT_DATE_FORMAT_PREFERENCE,
  parseDateFormatPreference,
} from "@/enums/date-format";
import { printHousePreferencesState } from "@/store/print-house-preferences";
import {
  DateFormatConfig,
  getDateFormatConfig,
} from "@/utils/date-format-config";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";

/** Read DATE_FORMAT from print-house preferences (loaded on auth validate). */
export function useDateFormatPreference(): {
  preference: DateFormatPreference;
  config: DateFormatConfig;
} {
  const preferences = useRecoilValue(printHousePreferencesState);
  const { GetPreferenceValueKey } = usePrintHousePreferences();

  const preference = useMemo(() => {
    const valueKey = GetPreferenceValueKey(EPreferences.DATE_FORMAT);
    if (valueKey) {
      return parseDateFormatPreference(valueKey);
    }
    const pref = preferences?.find(
      (p: { code?: string }) => p.code === EPreferences.DATE_FORMAT,
    );
    if (pref?.valueKey) {
      return parseDateFormatPreference(pref.valueKey);
    }
    if (pref?.value) {
      return parseDateFormatPreference(String(pref.value));
    }
    return DEFAULT_DATE_FORMAT_PREFERENCE;
  }, [preferences, GetPreferenceValueKey]);

  const config = useMemo(
    () => getDateFormatConfig(preference),
    [preference],
  );

  return { preference, config };
}

/** For non-React code: resolve preference from preferences array */
export function resolveDateFormatPreferenceFromList(
  preferences: Array<{ code?: string; value?: string; valueKey?: string; preferenceValues?: Array<{ id: string; valueKey?: string; value?: string }> }> | null | undefined,
): DateFormatPreference {
  if (!preferences?.length) {
    return DEFAULT_DATE_FORMAT_PREFERENCE;
  }
  const pref = preferences.find((p) => p.code === EPreferences.DATE_FORMAT);
  if (!pref) {
    return DEFAULT_DATE_FORMAT_PREFERENCE;
  }
  if (pref.preferenceValues && Array.isArray(pref.preferenceValues)) {
    const matched = pref.preferenceValues.find((opt) => opt.id === pref.value);
    if (matched?.valueKey) {
      return parseDateFormatPreference(matched.valueKey);
    }
    if (matched?.value) {
      return parseDateFormatPreference(matched.value);
    }
  }
  if (pref.valueKey) {
    return parseDateFormatPreference(pref.valueKey);
  }
  if (pref.value) {
    return parseDateFormatPreference(String(pref.value));
  }
  return DEFAULT_DATE_FORMAT_PREFERENCE;
}
